import { API_BASE_URL, DELIMITER } from "./constants";

/**
 * Standard filter param keys — if any of these appear in the URL,
 * they MUST also appear in `enabled_filters` for the API to apply them.
 */
const FILTER_KEYS = new Set([
  "availability", "brand_name", "stores",
  "cat_name", "subcat_name", "part_name", "vehicle_key",
]);

/**
 * Range param keys — same rule but for `enabled_ranges`.
 */
const RANGE_KEYS = new Set([
  "ratings_ranges", "reviews_ranges", "price_ranges",
]);

/**
 * Params that are NOT filter keys (UI-only or API plumbing).
 */
const NON_FILTER_PARAMS = new Set([
  "FilterNav", "expanded", "enabled_filters", "enabled_ranges",
  "hitsSize", "from", "refine_search", "search", "price_type",
]);

/**
 * Keys for the browser URL's enabled_filters/ranges when a tab is clicked.
 *
 * Level-1 sections (brands, stores, ratings, reviews, attributes):
 *   Data loads immediately on tab click → browse key goes into URL.
 *
 * Level-2 sections (categories, vehicle/fitment):
 *   Have expandable sub-levels → keys added only when sub-level expanded.
 *   Categories manages its own keys via CategoryFilter + buildSearchUrl preservation.
 */
function getSectionUrlBrowseKeys(
  section: string,
): { filters?: string[]; ranges?: string[] } | null {
  switch (section) {
    case "brands":
      return { filters: ["brand_name"] };
    case "ratings":
      return { ranges: ["ratings_ranges"] };
    case "reviews":
      return { ranges: ["reviews_ranges"] };
    case "stores":
      return { filters: ["stores"] };
    case "price":
      return { ranges: ["price_ranges"] };
    case "attributes":
      return null;  // Don't pollute URL with all keys; API fetch uses getSectionFetchKeys
    // Level-2: categories & vehicle manage their own keys
    case "categories":
    case "vehicle":
    case "availability":
    case "refine_search":
    default:
      return null;
  }
}

/**
 * Keys for FilterPanel's sidebar API fetch (buildSidebarQs).
 * Same as URL browse keys for Level-1 sections.
 * Returns null for Level-2 sections (categories, vehicle) —
 * they manage their own fetching independently.
 */
function getSectionFetchKeys(
  section: string,
): { filters?: string[]; ranges?: string[] } | null {
  if (section === "categories") return null;
  if (section === "attributes") return null; // 2-level: AttributesFilter manages its own fetches
  return getSectionUrlBrowseKeys(section);
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * Scan URL params and return the set of enabled_filters needed
 * for all applied filters to take effect.
 */
function computeAppliedFilters(sp: URLSearchParams): Set<string> {
  const ef = new Set<string>();
  sp.forEach((_val, key) => {
    if (FILTER_KEYS.has(key)) {
      ef.add(key);
    } else if (!NON_FILTER_PARAMS.has(key) && !RANGE_KEYS.has(key)) {
      // Unknown key → probably an attribute group param
      ef.add(key);
    }
  });
  return ef;
}

/**
 * Scan URL params and return the set of enabled_ranges needed
 * for all applied range filters to take effect.
 */
function computeAppliedRanges(sp: URLSearchParams): Set<string> {
  const er = new Set<string>();
  sp.forEach((_val, key) => {
    if (RANGE_KEYS.has(key)) er.add(key);
  });
  return er;
}

function cleanUiParams(sp: URLSearchParams) {
  sp.delete("FilterNav");
  sp.delete("expanded");
  // Remove any stale enabled_filters/ranges from the URL —
  // we always compute them fresh
  sp.delete("enabled_filters");
  sp.delete("enabled_ranges");
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Build query string for fetching PRODUCTS.
 * Computes enabled_filters/ranges from applied filter params so
 * the API actually applies them. Does NOT request extra aggregations.
 */
export function buildProductsQs(paramsString: string, from = 0): string {
  const sp = new URLSearchParams(paramsString);
  cleanUiParams(sp);

  const ef = computeAppliedFilters(sp);
  const er = computeAppliedRanges(sp);

  if (ef.size > 0) sp.set("enabled_filters", Array.from(ef).join(DELIMITER));
  if (er.size > 0) sp.set("enabled_ranges", Array.from(er).join(DELIMITER));

  sp.set("hitsSize", "20");
  sp.set("from", String(from));

  return sp.toString();
}

/**
 * Build query string for fetching a SIDEBAR SECTION's aggregation data.
 * Includes all applied filters (so aggregations respect them) PLUS
 * the section's own keys (so the API returns its options).
 *
 * For categories, this is CASCADING:
 *  - No cat_name applied → only enables cat_name
 *  - cat_name applied → enables cat_name + subcat_name
 *  - Both applied → enables cat_name + subcat_name + part_name
 *
 * Returns null for sections that don't need an API call.
 */
export function buildSidebarQs(paramsString: string, section: string): string | null {
  const config = getSectionFetchKeys(section);
  if (!config) return null;

  const sp = new URLSearchParams(paramsString);

  // 1. Start from existing enabled_filters/enabled_ranges (URL is source of truth)
  const ef = new Set(sp.get("enabled_filters")?.split(DELIMITER).filter(Boolean) ?? []);
  const er = new Set(sp.get("enabled_ranges")?.split(DELIMITER).filter(Boolean) ?? []);

  cleanUiParams(sp);

  // 2. Add keys for any applied filter/range values
  const appliedEf = computeAppliedFilters(sp);
  const appliedEr = computeAppliedRanges(sp);
  for (const f of appliedEf) ef.add(f);
  for (const r of appliedEr) er.add(r);

  // 3. Add the section's own keys so we get its aggregation data
  if (config.filters) for (const f of config.filters) ef.add(f);
  if (config.ranges) for (const r of config.ranges) er.add(r);

  if (ef.size > 0) sp.set("enabled_filters", Array.from(ef).join(DELIMITER));
  if (er.size > 0) sp.set("enabled_ranges", Array.from(er).join(DELIMITER));

  sp.set("hitsSize", "1");
  sp.set("from", "0");

  return sp.toString();
}

/**
 * Get URL browse keys for a section. Used by buildSearchUrl to include
 * Level-1 section keys in the browser URL's enabled_filters/ranges.
 */
export function getSectionUrlKeys(
  section: string,
): { filters?: string[]; ranges?: string[] } | null {
  return getSectionUrlBrowseKeys(section);
}

/**
 * Build query string for fetching the ATTRIBUTES group list (Level 1).
 * Just uses whatever's in the URL — no extra keys injected.
 */
export function buildAttributeGroupsQs(paramsString: string): string {
  const sp = new URLSearchParams(paramsString);

  const ef = new Set(sp.get("enabled_filters")?.split(DELIMITER).filter(Boolean) ?? []);
  const er = new Set(sp.get("enabled_ranges")?.split(DELIMITER).filter(Boolean) ?? []);

  cleanUiParams(sp);

  const appliedEf = computeAppliedFilters(sp);
  const appliedEr = computeAppliedRanges(sp);
  for (const f of appliedEf) ef.add(f);
  for (const r of appliedEr) er.add(r);

  if (ef.size > 0) sp.set("enabled_filters", Array.from(ef).join(DELIMITER));
  if (er.size > 0) sp.set("enabled_ranges", Array.from(er).join(DELIMITER));

  sp.set("hitsSize", "1");
  sp.set("from", "0");

  return sp.toString();
}

/**
 * Build query string for fetching a single ATTRIBUTE GROUP's items (Level 2).
 * Uses URL state + adds the specific group name to enabled_filters.
 */
export function buildAttributeItemsQs(paramsString: string, groupName: string): string {
  const sp = new URLSearchParams(paramsString);

  const ef = new Set(sp.get("enabled_filters")?.split(DELIMITER).filter(Boolean) ?? []);
  const er = new Set(sp.get("enabled_ranges")?.split(DELIMITER).filter(Boolean) ?? []);

  cleanUiParams(sp);

  const appliedEf = computeAppliedFilters(sp);
  const appliedEr = computeAppliedRanges(sp);
  for (const f of appliedEf) ef.add(f);
  for (const r of appliedEr) er.add(r);

  // Only add this group's key
  ef.add(groupName);

  if (ef.size > 0) sp.set("enabled_filters", Array.from(ef).join(DELIMITER));
  if (er.size > 0) sp.set("enabled_ranges", Array.from(er).join(DELIMITER));

  sp.set("hitsSize", "1");
  sp.set("from", "0");

  return sp.toString();
}

/**
 * Build query string for fetching a single CATEGORY LEVEL's aggregation data.
 * Used by CategoryFilter to independently fetch cat_name, subcat_name, or part_name.
 * Includes all applied filters so aggregations respect them.
 */
export function buildCategoryLevelQs(
  paramsString: string,
  level: "cat_name" | "subcat_name" | "part_name"
): string {
  const sp = new URLSearchParams(paramsString);

  // 1. Start from existing enabled_filters/enabled_ranges (URL is source of truth)
  const ef = new Set(sp.get("enabled_filters")?.split(DELIMITER).filter(Boolean) ?? []);
  const er = new Set(sp.get("enabled_ranges")?.split(DELIMITER).filter(Boolean) ?? []);

  cleanUiParams(sp);

  // 2. Add keys for any applied filter/range values
  const appliedEf = computeAppliedFilters(sp);
  const appliedEr = computeAppliedRanges(sp);
  for (const f of appliedEf) ef.add(f);
  for (const r of appliedEr) er.add(r);

  // 3. Add this level so the API returns its aggregation data
  ef.add(level);

  if (ef.size > 0) sp.set("enabled_filters", Array.from(ef).join(DELIMITER));
  if (er.size > 0) sp.set("enabled_ranges", Array.from(er).join(DELIMITER));

  sp.set("hitsSize", "1");
  sp.set("from", "0");

  return sp.toString();
}

/**
 * Build a full search query string (proxy mode — asks for ALL aggregations).
 */
export function buildSearchQs(paramsString: string, from = 0): string {
  const sp = new URLSearchParams(paramsString);

  const ef = new Set(sp.get("enabled_filters")?.split(DELIMITER).filter(Boolean) ?? []);
  for (const f of FILTER_KEYS) ef.add(f);
  sp.set("enabled_filters", Array.from(ef).join(DELIMITER));

  const er = new Set(sp.get("enabled_ranges")?.split(DELIMITER).filter(Boolean) ?? []);
  for (const r of RANGE_KEYS) er.add(r);
  sp.set("enabled_ranges", Array.from(er).join(DELIMITER));

  if (!sp.has("hitsSize")) sp.set("hitsSize", "20");
  if (!sp.has("from")) sp.set("from", String(from));

  sp.delete("FilterNav");
  sp.delete("expanded");

  return sp.toString();
}

// ── URL builders ─────────────────────────────────────────────────────

export function clientSearchUrl(queryString: string): string {
  return `${API_BASE_URL}/search_pg_one?${queryString}`;
}

export function clientCompletionUrl(search: string): string {
  return `${API_BASE_URL}/search_completion?search=${encodeURIComponent(search)}`;
}

export function clientAttributesUrl(queryString: string): string {
  return `${API_BASE_URL}/search_pg_one?${queryString}`;
}
